'use client';
import { useState, useEffect } from 'react';
import AppProvidersWrapper from "@/components/wrappers/AppProvidersWrapper";
import { Container, Row, Col, Card, Badge, Button, Spinner, Form, Offcanvas, ListGroup } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { fetchWrapper } from "@/helpers/fetch-wrapper";
import Swal from 'sweetalert2';
import moment from 'moment';
import 'moment/locale/ro';
import { checkIsNotLogged } from '@/utils/authCheck'; 
moment.locale('ro');


const Dashboard = () => {
     checkIsNotLogged('/auth/login');
       
     const [tasks, setTasks] = useState([]);
     const [projects, setProjects] = useState([]);
     const [loading, setLoading] = useState(true);
     const [selectedTask, setSelectedTask] = useState(null);
     const [showTaskPanel, setShowTaskPanel] = useState(false);
     const [currentUser, setCurrentUser] = useState(null);
     const [filter, setFilter] = useState('all'); // all, pending, in_progress, completed
     const [sortBy, setSortBy] = useState('priority'); // priority, dueDate, project

     useEffect(() => {
          loadData();
     }, []);

     const loadData = async () => {
          try {
               setLoading(true);
               const [tasksResponse, projectsResponse, userResponse] = await Promise.all([
                    fetchWrapper.get('/api/tasks?myTasks=1'),
                    fetchWrapper.get('/api/projects'),
                    fetchWrapper.get('/api/admin/current')
               ]);

               setTasks(tasksResponse.tasks || []);
               setProjects(projectsResponse.projects || []);
               setCurrentUser(userResponse);
          } catch (error) {
               console.error('Error loading data:', error);
               toast.error('Eroare la încărcarea datelor.');
          } finally {
               setLoading(false);
          }
     };

     const handleTaskClick = (task) => {
          setSelectedTask(task);
          setShowTaskPanel(true);
     };

     const handleStartTask = async (task) => {
          try {
               await fetchWrapper.put('/api/tasks', {
                    taskID: task.id,
                    status: 'in_progress'
               });
               toast.success('Task-ul a fost marcat ca în progres!');
               loadData();
               if (selectedTask?.id === task.id) {
                    setSelectedTask({ ...task, status: 'in_progress' });
               }
          } catch (error) {
               console.error('Error starting task:', error);
               toast.error(error || 'Eroare la actualizarea task-ului.');
          }
     };

     const handleCompleteTask = async (task) => {
          const result = await Swal.fire({
               title: 'Finalizare task',
               html: `
                    <p>Marchezi task-ul <strong>"${task.title}"</strong> ca finalizat?</p>
                    <div class="mb-3">
                         <label class="form-label">Timp lucrat (ore):</label>
                         <input type="number" id="timeSpent" class="form-control" step="0.5" min="0" placeholder="Ex: 2.5">
                    </div>
                    <div class="mb-3">
                         <label class="form-label">Notițe (opțional):</label>
                         <textarea id="notes" class="form-control" rows="3" placeholder="Adaugă detalii despre finalizare..."></textarea>
                    </div>
                    <div class="mb-3">
                         <label class="form-label">URL dovadă (opțional):</label>
                         <input type="url" id="proofUrl" class="form-control" placeholder="https://...">
                         <small class="text-muted">Link către fișier de pe AWS S3 sau alt serviciu</small>
                    </div>
               `,
               icon: 'question',
               showCancelButton: true,
               confirmButtonColor: '#28a745',
               cancelButtonColor: '#6c757d',
               confirmButtonText: 'Finalizează task',
               cancelButtonText: 'Anulează',
               preConfirm: () => {
                    return {
                         timeSpent: document.getElementById('timeSpent').value,
                         notes: document.getElementById('notes').value,
                         proofUrl: document.getElementById('proofUrl').value
                    };
               }
          });

          if (result.isConfirmed) {
               try {
                    await fetchWrapper.post('/api/tasks/complete', {
                         taskID: task.id,
                         timeSpent: result.value.timeSpent || null,
                         notes: result.value.notes || null,
                         proofUrl: result.value.proofUrl || null
                    });
                    toast.success('Task-ul a fost finalizat cu succes! 🎉');
                    loadData();
                    setShowTaskPanel(false);
               } catch (error) {
                    console.error('Error completing task:', error);
                    toast.error(error || 'Eroare la finalizarea task-ului.');
               }
          }
     };

     const getPriorityBadge = (priority) => {
          const variants = {
               low: 'secondary',
               medium: 'info',
               high: 'warning',
               critical: 'danger'
          };
          const labels = {
               low: 'Scăzută',
               medium: 'Medie',
               high: 'Ridicată',
               critical: 'Critică'
          };
          return <Badge bg={variants[priority]}>{labels[priority]}</Badge>;
     };

     const getStatusBadge = (status) => {
          const variants = {
               pending: 'secondary',
               in_progress: 'primary',
               blocked: 'danger',
               completed: 'success',
               cancelled: 'dark'
          };
          const labels = {
               pending: 'În așteptare',
               in_progress: 'În progres',
               blocked: 'Blocat',
               completed: 'Finalizat',
               cancelled: 'Anulat'
          };
          return <Badge bg={variants[status]}>{labels[status]}</Badge>;
     };

     const isTaskBlocked = (task) => {
          if (!task.requiresPreviousCompletion) return false;
          
          const projectTasks = tasks.filter(t => t.projectID === task.projectID);
          const previousTask = projectTasks
               .filter(t => t.orderIndex < task.orderIndex)
               .sort((a, b) => b.orderIndex - a.orderIndex)[0];
          
          return previousTask && previousTask.status !== 'completed';
     };

     const isMyTask = (task) => {
          return task.assignedUsers?.some(u => u.id === currentUser?.id);
     };

     const getFilteredTasks = () => {
          let filtered = tasks;

          if (filter !== 'all') {
               filtered = filtered.filter(t => t.status === filter);
          }

          if (sortBy === 'priority') {
               const priorityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
               filtered = [...filtered].sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
          } else if (sortBy === 'dueDate') {
               filtered = [...filtered].sort((a, b) => {
                    if (!a.dueDate) return 1;
                    if (!b.dueDate) return -1;
                    return new Date(a.dueDate) - new Date(b.dueDate);
               });
          } else if (sortBy === 'project') {
               filtered = [...filtered].sort((a, b) => {
                    const projectA = a.project?.name || '';
                    const projectB = b.project?.name || '';
                    return projectA.localeCompare(projectB);
               });
          }

          return filtered;
     };

     if (loading) {
          return (
               <AppProvidersWrapper>
                    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
                         <Spinner animation="border" role="status" variant="primary">
                              <span className="visually-hidden">Se încarcă...</span>
                         </Spinner>
                    </Container>
               </AppProvidersWrapper>
          );
     }

     const filteredTasks = getFilteredTasks();
     const pendingCount = tasks.filter(t => t.status === 'pending').length;
     const inProgressCount = tasks.filter(t => t.status === 'in_progress').length;
     const completedCount = tasks.filter(t => t.status === 'completed').length;

     return (
          <AppProvidersWrapper>
               <Container fluid className="py-4">
                    <Row className="mb-4">
                         <Col>
                              <h1 className="display-5 mb-2">
                                   <i className="bi bi-clipboard-check me-3"></i>
                                   Task-urile mele
                              </h1>
                              <p className="text-muted">Gestionează și urmărește task-urile tale</p>
                         </Col>
                    </Row>

     
                    <Row className="mb-4">
                         <Col md={3}>
                              <Card className="border-0 shadow-sm text-center">
                                   <Card.Body>
                                        <h3 className="text-secondary mb-2">{pendingCount}</h3>
                                        <p className="text-muted mb-0">În așteptare</p>
                                   </Card.Body>
                              </Card>
                         </Col>
                         <Col md={3}>
                              <Card className="border-0 shadow-sm text-center">
                                   <Card.Body>
                                        <h3 className="text-primary mb-2">{inProgressCount}</h3>
                                        <p className="text-muted mb-0">În progres</p>
                                   </Card.Body>
                              </Card>
                         </Col>
                         <Col md={3}>
                              <Card className="border-0 shadow-sm text-center">
                                   <Card.Body>
                                        <h3 className="text-success mb-2">{completedCount}</h3>
                                        <p className="text-muted mb-0">Finalizate</p>
                                   </Card.Body>
                              </Card>
                         </Col>
                         <Col md={3}>
                              <Card className="border-0 shadow-sm text-center">
                                   <Card.Body>
                                        <h3 className="text-info mb-2">{tasks.length}</h3>
                                        <p className="text-muted mb-0">Total</p>
                                   </Card.Body>
                              </Card>
                         </Col>
                    </Row>

         
                    <Row className="mb-4">
                         <Col md={6}>
                              <Form.Group>
                                   <Form.Label className="fw-bold">
                                        <i className="bi bi-funnel me-2"></i>
                                        Filtrează după status
                                   </Form.Label>
                                   <Form.Select 
                                        value={filter} 
                                        onChange={(e) => setFilter(e.target.value)}
                                   >
                                        <option value="all">Toate task-urile</option>
                                        <option value="pending">În așteptare</option>
                                        <option value="in_progress">În progres</option>
                                        <option value="completed">Finalizate</option>
                                   </Form.Select>
                              </Form.Group>
                         </Col>
                         <Col md={6}>
                              <Form.Group>
                                   <Form.Label className="fw-bold">
                                        <i className="bi bi-sort-down me-2"></i>
                                        Sortează după
                                   </Form.Label>
                                   <Form.Select 
                                        value={sortBy} 
                                        onChange={(e) => setSortBy(e.target.value)}
                                   >
                                        <option value="priority">Prioritate</option>
                                        <option value="dueDate">Termen limită</option>
                                        <option value="project">Proiect</option>
                                   </Form.Select>
                              </Form.Group>
                         </Col>
                    </Row>

                    <Row>
                         {filteredTasks.length === 0 ? (
                              <Col>
                                   <Card className="border-0 shadow-sm">
                                        <Card.Body className="text-center py-5">
                                             <i className="bi bi-inbox display-1 text-muted"></i>
                                             <h4 className="mt-3">Niciun task găsit</h4>
                                             <p className="text-muted">
                                                  {filter === 'all' 
                                                       ? 'Nu ai task-uri atribuite momentan.' 
                                                       : 'Nu există task-uri cu acest status.'}
                                             </p>
                                        </Card.Body>
                                   </Card>
                              </Col>
                         ) : (
                              filteredTasks.map(task => {
                                   const blocked = isTaskBlocked(task);
                                   const myTask = isMyTask(task);
                                   const overdue = task.dueDate && new Date(task.dueDate) < new Date() && task.status !== 'completed';

                                   return (
                                        <Col md={6} lg={4} xl={3} key={task.id} className="mb-4">
                                             <Card 
                                                  className={`border-0 shadow-sm h-100 ${myTask ? 'border-start border-primary border-5' : ''} ${blocked ? 'opacity-75' : ''}`}
                                                  style={{ 
                                                       cursor: 'pointer',
                                                       transition: 'transform 0.2s',
                                                       ...(myTask && {
                                                            boxShadow: '0 0 15px rgba(13, 110, 253, 0.3)',
                                                            border: '2px solid #0d6efd'
                                                       })
                                                  }}
                                                  onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                  onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}
                                                  onClick={() => handleTaskClick(task)}
                                             >
                                                  <Card.Body>
                                                       <div className="d-flex justify-content-between align-items-start mb-3">
                                                            {getPriorityBadge(task.priority)}
                                                            {myTask && (
                                                                 <Badge bg="primary" pill>
                                                                      <i className="bi bi-star-fill"></i> Al meu
                                                                 </Badge>
                                                            )}
                                                       </div>

                                                       <h5 className="mb-2">{task.title}</h5>
                                                       
                                                       {task.project && (
                                                            <p className="text-muted small mb-2">
                                                                 <i className="bi bi-folder me-2"></i>
                                                                 {task.project.name}
                                                            </p>
                                                       )}

                                                       {task.dueDate && (
                                                            <p className={`small mb-2 ${overdue ? 'text-danger fw-bold' : 'text-muted'}`}>
                                                                 <i className={`bi bi-calendar-event me-2 ${overdue ? 'text-danger' : ''}`}></i>
                                                                 {overdue && <i className="bi bi-exclamation-triangle me-1"></i>}
                                                                 {moment(task.dueDate).format('DD MMMM YYYY')}
                                                            </p>
                                                       )}

                                                       <div className="mt-3">
                                                            {getStatusBadge(task.status)}
                                                            {blocked && (
                                                                 <Badge bg="danger" className="ms-2">
                                                                      <i className="bi bi-lock-fill me-1"></i>
                                                                      Blocat
                                                                 </Badge>
                                                            )}
                                                       </div>

                                                       {task.status === 'pending' && !blocked && myTask && (
                                                            <Button 
                                                                 variant="outline-primary" 
                                                                 size="sm" 
                                                                 className="w-100 mt-3"
                                                                 onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      handleStartTask(task);
                                                                 }}
                                                            >
                                                                 <i className="bi bi-play-fill me-2"></i>
                                                                 Începe lucrul
                                                            </Button>
                                                       )}

                                                       {task.status === 'in_progress' && myTask && (
                                                            <Button 
                                                                 variant="success" 
                                                                 size="sm" 
                                                                 className="w-100 mt-3"
                                                                 onClick={(e) => {
                                                                      e.stopPropagation();
                                                                      handleCompleteTask(task);
                                                                 }}
                                                            >
                                                                 <i className="bi bi-check-circle-fill me-2"></i>
                                                                 Finalizează
                                                            </Button>
                                                       )}
                                                  </Card.Body>
                                             </Card>
                                        </Col>
                                   );
                              })
                         )}
                    </Row>
               </Container>


               <Offcanvas 
                    show={showTaskPanel} 
                    onHide={() => setShowTaskPanel(false)} 
                    placement="end"
                    style={{ width: '500px' }}
               >
                    <Offcanvas.Header closeButton>
                         <Offcanvas.Title>Detalii task</Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                         {selectedTask && (
                              <div>
                                   <h4 className="mb-3">{selectedTask.title}</h4>

                                   <div className="mb-3">
                                        <strong>Status:</strong>
                                        <div className="mt-2">
                                             {getStatusBadge(selectedTask.status)}
                                        </div>
                                   </div>

                                   <div className="mb-3">
                                        <strong>Prioritate:</strong>
                                        <div className="mt-2">
                                             {getPriorityBadge(selectedTask.priority)}
                                        </div>
                                   </div>

                                   {selectedTask.project && (
                                        <div className="mb-3">
                                             <strong>Proiect:</strong>
                                             <p className="mb-0 mt-2">{selectedTask.project.name}</p>
                                        </div>
                                   )}

                                   {selectedTask.description && (
                                        <div className="mb-3">
                                             <strong>Descriere:</strong>
                                             <p className="mt-2">{selectedTask.description}</p>
                                        </div>
                                   )}

                                   {selectedTask.dueDate && (
                                        <div className="mb-3">
                                             <strong>Termen limită:</strong>
                                             <p className="mb-0 mt-2">
                                                  {moment(selectedTask.dueDate).format('DD MMMM YYYY')}
                                             </p>
                                        </div>
                                   )}

                                   {selectedTask.estimatedHours && (
                                        <div className="mb-3">
                                             <strong>Ore estimate:</strong>
                                             <p className="mb-0 mt-2">{selectedTask.estimatedHours}h</p>
                                        </div>
                                   )}

                                   {selectedTask.assignedUsers && selectedTask.assignedUsers.length > 0 && (
                                        <div className="mb-3">
                                             <strong>Atribuit către:</strong>
                                             <ListGroup className="mt-2">
                                                  {selectedTask.assignedUsers.map(user => (
                                                       <ListGroup.Item key={user.id} className="d-flex align-items-center gap-2">
                                                            {user.avatar ? (
                                                                 <img 
                                                                      src={user.avatar} 
                                                                      alt={user.name} 
                                                                      style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                                                 />
                                                            ) : (
                                                                 <div 
                                                                      className="bg-secondary text-white d-flex align-items-center justify-content-center"
                                                                      style={{ width: '32px', height: '32px', borderRadius: '50%' }}
                                                                 >
                                                                      {user.name?.charAt(0) || user.email?.charAt(0)}
                                                                 </div>
                                                            )}
                                                            <span>{user.name || user.email}</span>
                                                       </ListGroup.Item>
                                                  ))}
                                             </ListGroup>
                                        </div>
                                   )}

                                   {selectedTask.notes && (
                                        <div className="mb-3">
                                             <strong>Notițe:</strong>
                                             <Card className="mt-2">
                                                  <Card.Body className="bg-light">
                                                       <small>{selectedTask.notes}</small>
                                                  </Card.Body>
                                             </Card>
                                        </div>
                                   )}

                                   {selectedTask.completions && selectedTask.completions.length > 0 && (
                                        <div className="mb-3">
                                             <strong>Istoric finalizare:</strong>
                                             {selectedTask.completions.map((completion, idx) => (
                                                  <Card key={idx} className="mt-2">
                                                       <Card.Body>
                                                            <p className="mb-1">
                                                                 <strong>{completion.user.name || completion.user.email}</strong>
                                                            </p>
                                                            <p className="text-muted small mb-2">
                                                                 {moment(completion.completedAt).format('DD MMMM YYYY, HH:mm')}
                                                            </p>
                                                            {completion.timeSpent && (
                                                                 <p className="mb-1">Timp: {completion.timeSpent}h</p>
                                                            )}
                                                            {completion.notes && (
                                                                 <p className="mb-1">{completion.notes}</p>
                                                            )}
                                                            {completion.proofUrl && (
                                                                 <a href={completion.proofUrl} target="_blank" rel="noopener noreferrer">
                                                                      Vezi dovada
                                                                 </a>
                                                            )}
                                                       </Card.Body>
                                                  </Card>
                                             ))}
                                        </div>
                                   )}

                                   <div className="d-grid gap-2 mt-4">
                                        {selectedTask.status === 'pending' && !isTaskBlocked(selectedTask) && isMyTask(selectedTask) && (
                                             <Button 
                                                  variant="primary"
                                                  onClick={() => {
                                                       handleStartTask(selectedTask);
                                                       setShowTaskPanel(false);
                                                  }}
                                             >
                                                  <i className="bi bi-play-fill me-2"></i>
                                                  Începe lucrul
                                             </Button>
                                        )}

                                        {selectedTask.status === 'in_progress' && isMyTask(selectedTask) && (
                                             <Button 
                                                  variant="success"
                                                  onClick={() => handleCompleteTask(selectedTask)}
                                             >
                                                  <i className="bi bi-check-circle-fill me-2"></i>
                                                  Finalizează task
                                             </Button>
                                        )}
                                   </div>
                              </div>
                         )}
                    </Offcanvas.Body>
               </Offcanvas>
          </AppProvidersWrapper>
     );
};

export default Dashboard;